import {
  AIM_ASSIST_DEG,
  BLAST_TTL_TICKS,
  CRATE_PUSH_FACTOR,
  CURVE_MAX_CHORD,
  HIT_INVULNERABLE_TICKS,
  HIT_PENALTY_MS,
  INITIAL_PICKUPS,
  MUZZLE_GAP,
  NPC_HIT_PENALTY_MS,
  PICKUP_RADIUS,
  PICKUP_SPAWN_CLEARANCE,
  PICKUP_SPAWN_ATTEMPTS,
  PILLAR_RADIUS_LARGE,
  PILLAR_RADIUS_SMALL,
  PLAYER_RADIUS,
  RESPAWN_DELAY_TICKS,
  TICK_HZ,
  TILE,
  WEAPON_ZONE_WEIGHTS,
  WEAPONS,
  WEAPON_TYPES,
} from './constants';
import { distanceField } from './navigation';
import { hitNpcBox } from './npcBoxes';
import { castRayFree, isFree, pillarAt, segmentHitsCircle } from './obstacles';
import { applyImpulse } from './physics';
import { drawInt, drawRange } from './rng';
import { circleFits, mapWidth, mapHeight, tileAt, tileCenter } from './tilemap';
import type {
  Beam,
  BeamWeaponSpec,
  Body,
  Circle,
  NpcBox,
  Pickup,
  Player,
  PlayerId,
  Projectile,
  ProjectileWeaponSpec,
  State,
  WeaponTier,
  Vec2,
  WeaponSpec,
  WeaponType,
} from './types';
import { dist, distToSegment, dot, normalize, rayCircle, rotate, scale, sub } from './vec';

// Weapons: pickups and their spawn plan, firing with aim assist and recoil, projectile flight
// (straight, bouncing, curving), bursts, beams, blasts, hits on crates, players, NPCs and boxes. All functions
// mutate a step draft.

const AIM_ASSIST_COS = Math.cos((AIM_ASSIST_DEG * Math.PI) / 180);

export function opponentOf(id: PlayerId): PlayerId {
  return id === 0 ? 1 : 0;
}

/** The spec of a weapon that fires projectiles; projectiles only ever carry such weapons. */
export function projectileSpec(weapon: WeaponType): ProjectileWeaponSpec {
  const spec = WEAPONS[weapon];
  if (spec.kind !== 'projectile') throw new Error(`${weapon} fires no projectiles`);
  return spec;
}

/** The spec of a beam weapon; beams only ever carry such weapons. */
export function beamSpec(weapon: WeaponType): BeamWeaponSpec {
  const spec = WEAPONS[weapon];
  if (spec.kind !== 'beam') throw new Error(`${weapon} fires no beam`);
  return spec;
}

// --- pickups ------------------------------------------------------------------

/** The pillar rectangles retain the arena's three zones; other maps use their nearest tier marker. */
export function weaponZoneAt(s: State, pos: Vec2): WeaponTier {
  const inside = (radius: number): boolean => {
    const pillars = s.pillars.filter(p => p.radius === radius);
    return pillars.length >= 4
      && pos.x >= Math.min(...pillars.map(p => p.pos.x))
      && pos.x <= Math.max(...pillars.map(p => p.pos.x))
      && pos.y >= Math.min(...pillars.map(p => p.pos.y))
      && pos.y <= Math.max(...pillars.map(p => p.pos.y));
  };
  if (s.pillars.filter(p => p.radius === PILLAR_RADIUS_LARGE).length >= 4) {
    return inside(PILLAR_RADIUS_SMALL) ? 3 : inside(PILLAR_RADIUS_LARGE) ? 2 : 1;
  }
  let nearest = s.spawnSpots[0];
  for (const spot of s.spawnSpots) {
    if (!nearest || dist(pos, spot.pos) < dist(pos, nearest.pos)) nearest = spot;
  }
  return nearest?.tier ?? 1;
}

function pickupFits(s: State, pos: Vec2): boolean {
  if (!isFree(s.map, s.pillars, pos.x, pos.y, PICKUP_RADIUS)) return false;
  if ([...s.crates, ...s.npcBoxes, ...s.players, ...s.npcs].some(body => dist(pos, body.pos) < PICKUP_RADIUS + body.radius)) return false;
  return ![...s.pickups, ...s.spawnPlan].some(p => dist(pos, p.pos) < PICKUP_SPAWN_CLEARANCE);
}

/** Continuous positions on reachable floor, with a bounded search and a tile-centre fallback. */
function drawPickupPosition(s: State): Vec2 | null {
  const reachable = distanceField(s, s.arenaSetup.starts[0]);
  const fits = (pos: Vec2): boolean => {
    const tile = tileAt(pos);
    const centre = tileCenter(tile.x, tile.y);
    return reachable[tile.y * s.map.width + tile.x] >= 0 && pickupFits(s, pos)
      && !s.pillars.some(p => segmentHitsCircle(centre, pos, { pos: p.pos, radius: p.radius + PLAYER_RADIUS }));
  };
  for (let attempt = 0; attempt < PICKUP_SPAWN_ATTEMPTS; attempt++) {
    const pos = {
      x: drawRange(s, TILE + PICKUP_RADIUS, mapWidth(s.map) - TILE - PICKUP_RADIUS),
      y: drawRange(s, TILE + PICKUP_RADIUS, mapHeight(s.map) - TILE - PICKUP_RADIUS),
    };
    if (fits(pos)) return pos;
  }
  const candidates: Vec2[] = [];
  for (let y = 1; y < s.map.height - 1; y++) {
    for (let x = 1; x < s.map.width - 1; x++) {
      const pos = tileCenter(x, y);
      if (fits(pos)) candidates.push(pos);
    }
  }
  return candidates.length ? candidates[drawInt(s, candidates.length)] : null;
}

/** The weapons of each tier in WEAPON_TYPES order. */
export const WEAPONS_BY_TIER: Record<WeaponTier, readonly WeaponType[]> = {
  1: WEAPON_TYPES.filter((w) => WEAPONS[w].tier === 1),
  2: WEAPON_TYPES.filter((w) => WEAPONS[w].tier === 2),
  3: WEAPON_TYPES.filter((w) => WEAPONS[w].tier === 3),
};

/** Zone weights affect quality, while every weapon remains possible anywhere. */
function drawWeapon(s: State, zone: WeaponTier): WeaponType {
  const weights = WEAPON_ZONE_WEIGHTS[zone];
  let roll = drawInt(s, weights.reduce((sum, weight) => sum + weight, 0));
  let tier: WeaponTier = 1;
  for (const weight of weights) {
    if (roll < weight) break;
    roll -= weight;
    tier = (tier + 1) as WeaponTier;
  }
  const pool = WEAPONS_BY_TIER[tier];
  return pool[drawInt(s, pool.length)];
}

/** Reserve a random location; if the arena is full, keep a pending entry to retry later. */
export function schedulePickup(s: State, atTick: number): void {
  // The border is always blocked, so an unplaced entry must be relocated before it materializes.
  const pos = drawPickupPosition(s) ?? { x: 0, y: 0 };
  s.spawnPlan.push({ atTick, pos, weapon: drawWeapon(s, weaponZoneAt(s, pos)) });
}

/** Markers retain the level's pickup count, but do not constrain spawn positions. */
export function placeInitialPickups(s: State): void {
  const n = Math.min(INITIAL_PICKUPS, s.spawnSpots.length);
  for (let i = 0; i < n; i++) schedulePickup(s, s.tick);
  materializePickups(s);
}

function materializePickups(s: State): void {
  const due = s.spawnPlan.filter((e) => e.atTick <= s.tick);
  if (due.length === 0) return;
  s.spawnPlan = s.spawnPlan.filter((e) => e.atTick > s.tick);
  for (const e of due) {
    // A moving crate or player may have occupied the reserved position during the delay.
    if (!pickupFits(s, e.pos)) {
      const pos = drawPickupPosition(s);
      if (!pos) {
        s.spawnPlan.push({ ...e, atTick: s.tick + 1 });
        continue;
      }
      e.pos = pos;
      e.weapon = drawWeapon(s, weaponZoneAt(s, pos));
    }
    s.pickups.push({ pos: { ...e.pos }, weapon: e.weapon });
  }
}

/** Players with an empty slot collect nearby pickups; the nearer player wins a tie. */
export function stepPickups(s: State): void {
  const remaining: Pickup[] = [];
  let taken = 0;
  for (const pickup of s.pickups) {
    let taker: Player | null = null;
    let best = Infinity;
    for (const player of s.players) {
      if (player.weapon !== null) continue;
      const d = dist(player.pos, pickup.pos);
      if (d <= PICKUP_RADIUS && d < best) {
        best = d;
        taker = player;
      }
    }
    if (taker) {
      taker.weapon = pickup.weapon;
      taken++;
    } else {
      remaining.push(pickup);
    }
  }
  s.pickups = remaining;
  for (let i = 0; i < taken; i++) schedulePickup(s, s.tick + RESPAWN_DELAY_TICKS);
  materializePickups(s);
}

// --- aiming -------------------------------------------------------------------

/**
 * The point a shot is aimed at: `fireAt`, or the opponent when `fireAt` lies within the aim assist
 * cone around them. A tap on the shooter's own position aims at the opponent.
 */
export function aimPoint(s: State, shooter: Player, fireAt: Vec2): Vec2 {
  const opponent = s.players[opponentOf(shooter.id)].pos;
  const toOpponent = normalize(sub(opponent, shooter.pos));
  const dir = normalize(sub(fireAt, shooter.pos));
  const hasOpponentDir = toOpponent.x !== 0 || toOpponent.y !== 0;
  if (dir.x === 0 && dir.y === 0) return hasOpponentDir ? opponent : { x: shooter.pos.x + 1, y: shooter.pos.y };
  if (hasOpponentDir && dot(dir, toOpponent) >= AIM_ASSIST_COS) return opponent;
  return fireAt;
}

/** Unit direction from the shooter to the aim point (see aimPoint). */
export function aimDirection(s: State, shooter: Player, fireAt: Vec2): Vec2 {
  const dir = normalize(sub(aimPoint(s, shooter, fireAt), shooter.pos));
  return dir.x === 0 && dir.y === 0 ? { x: 1, y: 0 } : dir;
}

/** Where a projectile leaves its shooter: just outside the player circle (see MUZZLE_GAP). */
export function muzzleDistance(spec: ProjectileWeaponSpec, shooterRadius = PLAYER_RADIUS): number {
  return shooterRadius + spec.projectileRadius + MUZZLE_GAP;
}

/**
 * The direction a curving round must leave in so that its arc passes through the point `distance`
 * ahead along `aim`. The round starts `muzzle` units out along the launch direction and then flies a
 * circle of radius R = speed / omega whose centre lies R to the turning side. Writing the launch angle
 * as alpha (measured from the aim, towards the side the round does not turn to), the condition that
 * the target lies on that circle reduces to m cos(alpha) - R sin(alpha) = (d^2 + m^2) / 2d, solved
 * below. The round flies the circle as a polygon (move a tick, then turn), whose edges lean half a
 * turn inward from the tangent, so half a tick's turn is added back. The distance is capped at
 * CURVE_MAX_CHORD; a straight-flying weapon leaves along `aim`.
 */
export function launchDirection(spec: ProjectileWeaponSpec, aim: Vec2, distance: number, muzzle = muzzleDistance(spec)): Vec2 {
  if (spec.curveDegPerSec === 0 || distance <= 0) return aim;
  const d = Math.min(distance, CURVE_MAX_CHORD);
  const omega = (Math.abs(spec.curveDegPerSec) * Math.PI) / 180;
  const radius = spec.projectileSpeed / omega;
  const rho = Math.sqrt(muzzle * muzzle + radius * radius);
  const phi = Math.atan2(radius, muzzle);
  const k = Math.min(1, (d * d + muzzle * muzzle) / (2 * d * rho));
  const alpha = Math.acos(k) - phi + omega / TICK_HZ / 2;
  return rotate(aim, Math.sign(spec.curveDegPerSec) * alpha);
}

// --- firing -------------------------------------------------------------------

function pelletAngle(spec: ProjectileWeaponSpec, i: number): number {
  if (spec.pattern === 'ring') return (i / spec.pellets) * 2 * Math.PI;
  return spec.pellets > 1 ? ((i / (spec.pellets - 1) - 0.5) * spec.spreadDeg * Math.PI) / 180 : 0;
}

/** The directions of a round's pellets: fanned over spreadDeg around `launch`, or evenly around the ring with the first along `launch`. */
export function pelletDirections(spec: ProjectileWeaponSpec, launch: Vec2): Vec2[] {
  const dirs: Vec2[] = [];
  for (let i = 0; i < spec.pellets; i++) dirs.push(rotate(launch, pelletAngle(spec, i)));
  return dirs;
}

/** A pellet as it leaves the shooter along `d`: at the muzzle, at full speed, with its whole life and all its bounces ahead. */
export function launchProjectile(shooter: Circle, ownerId: PlayerId, weapon: WeaponType, spec: ProjectileWeaponSpec, d: Vec2, fromNpc = false): Projectile {
  const startDist = muzzleDistance(spec, shooter.radius);
  return {
    pos: { x: shooter.pos.x + d.x * startDist, y: shooter.pos.y + d.y * startDist },
    vel: { x: d.x * spec.projectileSpeed, y: d.y * spec.projectileSpeed },
    weapon,
    ownerId,
    fromNpc,
    ttlTicks: spec.ttlTicks,
    bouncesLeft: spec.bounces,
  };
}

/** One round: its pellets leave from the shooter's current position, the shooter takes this round's share of the recoil. */
function fireRound(s: State, shooter: Player, weapon: WeaponType, spec: ProjectileWeaponSpec, launch: Vec2): void {
  for (const d of pelletDirections(spec, launch)) s.projectiles.push(launchProjectile(shooter, shooter.id, weapon, spec, d));
  applyImpulse(shooter, scale(launch, -spec.recoil / spec.burst));
}

/**
 * Fires the player's weapon at a world point if they hold one. There is no cooldown: the slot is
 * empty after the shot, and a pickup collected right away (e.g. in the recoil slide) fires at once.
 * A burst fires its first round now and queues the rest; a beam appears at once.
 */
export function fire(s: State, shooter: Player, fireAt: Vec2): void {
  if (shooter.weapon === null) return;
  const weapon = shooter.weapon;
  const spec = WEAPONS[weapon];
  const target = aimPoint(s, shooter, fireAt);
  const aim = aimDirection(s, shooter, fireAt);
  shooter.weapon = null;
  if (spec.kind === 'beam') {
    s.beams.push({
      from: { ...shooter.pos },
      dir: aim,
      length: castRayFree(s.map, s.pillars, shooter.pos, aim, spec.range),
      weapon,
      ownerId: shooter.id,
      ttlTicks: spec.ttlTicks,
      hit: false,
      struck: [],
    });
    applyImpulse(shooter, scale(aim, -spec.recoil));
    return;
  }
  const launch = launchDirection(spec, aim, dist(target, shooter.pos), muzzleDistance(spec, shooter.radius));
  fireRound(s, shooter, weapon, spec, launch);
  if (spec.burst > 1) s.bursts.push({ ownerId: shooter.id, weapon, dir: launch, left: spec.burst - 1, nextTick: s.tick + spec.burstIntervalTicks });
}

/** Fires the rounds of running bursts that are due this tick. */
export function stepBursts(s: State): void {
  const pending = [];
  for (const b of s.bursts) {
    if (b.nextTick > s.tick) {
      pending.push(b);
      continue;
    }
    const spec = projectileSpec(b.weapon);
    fireRound(s, s.players[b.ownerId], b.weapon, spec, b.dir);
    b.left--;
    if (b.left > 0) {
      b.nextTick = s.tick + spec.burstIntervalTicks;
      pending.push(b);
    }
  }
  s.bursts = pending;
}

export function stepTimers(s: State): void {
  for (const p of s.players) {
    if (p.invulnerableTicks > 0) p.invulnerableTicks--;
  }
}

// --- hits ---------------------------------------------------------------------

/** Knockback still applies during the shared invulnerability window. */
function strike(target: Player, dir: Vec2, spec: WeaponSpec, penaltyMs = HIT_PENALTY_MS): void {
  applyImpulse(target, scale(dir, spec.knockback));
  loseChessTime(target, penaltyMs);
}

/** All arena damage shares invulnerability and cumulative, clamped hit feedback. */
export function loseChessTime(target: Player, penaltyMs: number): void {
  if (target.invulnerableTicks > 0) return;
  const loss = Math.min(target.chessClockMs, penaltyMs);
  target.chessClockMs -= loss;
  target.hitTimeLostMs += loss;
  target.invulnerableTicks = HIT_INVULNERABLE_TICKS;
}

function touches(centre: Vec2, radius: number, body: Circle): boolean {
  return dist(centre, body.pos) < radius + body.radius;
}

/** Explodes a projectile where it is: everyone in range is shoved outwards, the opponent is hurt. */
function detonate(s: State, p: Projectile, spec: ProjectileWeaponSpec): void {
  const centre = { ...p.pos };
  const fallback = normalize(p.vel);
  const radial = (body: Body): Vec2 => {
    const dir = normalize(sub(body.pos, centre));
    return dir.x === 0 && dir.y === 0 ? fallback : dir;
  };
  s.blasts.push({ pos: centre, radius: spec.blastRadius, weapon: p.weapon, ownerId: p.ownerId, ttlTicks: BLAST_TTL_TICKS });
  for (const player of s.players) {
    if (!touches(centre, spec.blastRadius, player)) continue;
    if (player.id === p.ownerId) applyImpulse(player, scale(radial(player), spec.knockback));
    else strike(player, radial(player), spec);
  }
  s.npcs = s.npcs.filter(npc => {
    if (!touches(centre, spec.blastRadius, npc)) return true;
    if (npc.ownerId !== p.ownerId) return false;
    applyImpulse(npc, scale(radial(npc), spec.knockback));
    return true;
  });
  for (const box of [...s.npcBoxes]) {
    if (touches(centre, spec.blastRadius, box)) hitNpcBox(s, box, p.ownerId);
  }
  for (const crate of s.crates) {
    if (touches(centre, spec.blastRadius, crate)) applyImpulse(crate, scale(radial(crate), spec.knockback * CRATE_PUSH_FACTOR));
  }
}

// --- projectiles --------------------------------------------------------------

/** A curving round turns after each tick of flight, so its velocity is always the one for the next tick. */
function turn(p: Projectile, spec: ProjectileWeaponSpec): void {
  if (spec.curveDegPerSec !== 0) p.vel = rotate(p.vel, (spec.curveDegPerSec * Math.PI) / 180 / TICK_HZ);
}

/** Moves a projectile one tick; returns false when it hit a wall or pillar it could not bounce off. */
function flyProjectile(s: State, p: Projectile, spec: ProjectileWeaponSpec): boolean {
  const r = spec.projectileRadius;
  const dx = p.vel.x / TICK_HZ;
  const dy = p.vel.y / TICK_HZ;
  const pillar = pillarAt(s.pillars, p.pos.x + dx, p.pos.y + dy, r);
  if (!pillar && circleFits(s.map, p.pos.x + dx, p.pos.y + dy, r)) {
    p.pos.x += dx;
    p.pos.y += dy;
    turn(p, spec);
    return true;
  }
  if (p.bouncesLeft <= 0) return false;
  p.bouncesLeft--;
  if (pillar) {
    // mirror the velocity at the pillar's surface: the normal runs from its centre to the projectile
    const n = normalize(sub(p.pos, pillar.pos));
    const along = p.vel.x * n.x + p.vel.y * n.y;
    p.vel.x -= 2 * along * n.x;
    p.vel.y -= 2 * along * n.y;
  } else {
    // reflect the axis that is blocked on its own; a clean corner hit reflects both
    const xBlocked = !circleFits(s.map, p.pos.x + dx, p.pos.y, r);
    const yBlocked = !circleFits(s.map, p.pos.x, p.pos.y + dy, r);
    if (xBlocked || !yBlocked) p.vel.x = -p.vel.x;
    if (yBlocked || !xBlocked) p.vel.y = -p.vel.y;
  }
  const nx = p.pos.x + p.vel.x / TICK_HZ;
  const ny = p.pos.y + p.vel.y / TICK_HZ;
  if (isFree(s.map, s.pillars, nx, ny, r)) {
    p.pos.x = nx;
    p.pos.y = ny;
  }
  turn(p, spec);
  return true;
}

/**
 * Flight, wall handling and hits for every projectile. Own projectiles never hit the shooter. A
 * projectile with a blast explodes wherever it ends: wall, crate, box, enemy or the end of its life.
 */
export function stepProjectiles(s: State): void {
  const alive: Projectile[] = [];
  for (const p of s.projectiles) {
    const spec = projectileSpec(p.weapon);
    const explosive = spec.blastRadius > 0;
    p.ttlTicks--;
    if (p.ttlTicks <= 0) {
      if (explosive) detonate(s, p, spec);
      continue;
    }
    if (!flyProjectile(s, p, spec)) {
      if (explosive) detonate(s, p, spec);
      continue;
    }
    const box = s.npcBoxes.find(b => touches(p.pos, spec.projectileRadius, b));
    if (box) {
      if (explosive) detonate(s, p, spec);
      else hitNpcBox(s, box, p.ownerId);
      continue;
    }
    const target = s.players[opponentOf(p.ownerId)];
    if (touches(p.pos, spec.projectileRadius, target)) {
      if (explosive) detonate(s, p, spec);
      else strike(target, normalize(p.vel), spec, p.fromNpc ? NPC_HIT_PENALTY_MS : HIT_PENALTY_MS);
      continue;
    }
    const crate = s.crates.find((c) => touches(p.pos, spec.projectileRadius, c));
    if (crate) {
      if (explosive) detonate(s, p, spec);
      else applyImpulse(crate, scale(normalize(p.vel), spec.knockback * CRATE_PUSH_FACTOR));
      continue;
    }
    const npc = s.npcs.find(n => n.ownerId !== p.ownerId && touches(p.pos, spec.projectileRadius, n));
    if (npc) {
      if (explosive) detonate(s, p, spec);
      else s.npcs = s.npcs.filter(n => n !== npc);
      continue;
    }
    alive.push(p);
  }
  s.projectiles = alive;
}

/**
 * Where a pellet launched now along `dir` would fly: its positions tick by tick, from the muzzle to
 * where it dies (a wall it cannot bounce off, an enemy, a crate, a box or the end of its life), with the
 * flight rules of stepProjectiles. The last point is where it ends, so a blast would burst there.
 * For the client's shot preview; bodies that move in the meantime can of course change the real flight.
 */
export function traceProjectile(s: State, shooter: Player, weapon: WeaponType, dir: Vec2): Vec2[] {
  const spec = projectileSpec(weapon);
  const p = launchProjectile(shooter, shooter.id, weapon, spec, dir);
  const target = s.players[opponentOf(shooter.id)];
  const path: Vec2[] = [{ ...p.pos }];
  while (--p.ttlTicks > 0 && flyProjectile(s, p, spec)) {
    path.push({ ...p.pos });
    if (s.npcBoxes.some(b => touches(p.pos, spec.projectileRadius, b))
      || touches(p.pos, spec.projectileRadius, target)
      || s.npcs.some(n => n.ownerId !== shooter.id && touches(p.pos, spec.projectileRadius, n))
      || s.crates.some(c => touches(p.pos, spec.projectileRadius, c))) break;
  }
  return path;
}

// --- beams and blasts ---------------------------------------------------------

/** Current length of a beam: it grows from the shooter over growTicks, then stands at full length. */
export function beamLength(b: Beam): number {
  const spec = beamSpec(b.weapon);
  if (spec.growTicks <= 0) return b.length;
  const age = spec.ttlTicks - b.ttlTicks;
  return b.length * Math.min(1, age / spec.growTicks);
}

/** First contact also includes a circle overlapping the beam's origin. */
function beamContact(from: Vec2, dir: Vec2, body: Circle, width: number): number | null {
  const radius = body.radius + width;
  return dist(from, body.pos) <= radius ? 0 : rayCircle(from, dir, body.pos, radius);
}

/** Shared by damage, drawing and preview: a surviving box blocks the entire beam, including grazing hits. */
export function traceBeam(s: State, from: Vec2, dir: Vec2, maxLength: number, width: number): { length: number; box: NpcBox | null } {
  let length = castRayFree(s.map, s.pillars, from, dir, maxLength);
  let box: NpcBox | null = null;
  for (const candidate of s.npcBoxes) {
    const hit = beamContact(from, dir, candidate, width);
    if (hit !== null && hit <= length && (box === null || hit < length)) {
      length = hit;
      box = candidate;
    }
  }
  return { length, box };
}

/** Ages beams; each box, NPC and the opponent can be struck at most once over a beam's lifetime. */
export function stepBeams(s: State): void {
  const alive: Beam[] = [];
  for (const b of s.beams) {
    b.ttlTicks--;
    if (b.ttlTicks <= 0) continue;
    const spec = beamSpec(b.weapon);
    const { length, box } = traceBeam(s, b.from, b.dir, beamLength(b), spec.width);
    const tip = { x: b.from.x + b.dir.x * length, y: b.from.y + b.dir.y * length };
    if (box && !b.struck.includes(box.id)) {
      b.struck.push(box.id);
      hitNpcBox(s, box, b.ownerId);
    }
    if (!b.hit) {
      const target = s.players[opponentOf(b.ownerId)];
      const contact = beamContact(b.from, b.dir, target, spec.width);
      if (distToSegment(target.pos, b.from, tip) < spec.width + target.radius
        && (!box || (contact !== null && contact < length))) {
        strike(target, b.dir, spec);
        b.hit = true;
      }
    }
    s.npcs = s.npcs.filter(npc => {
      if (npc.ownerId === b.ownerId || b.struck.includes(npc.id)) return true;
      const contact = beamContact(b.from, b.dir, npc, spec.width);
      if (distToSegment(npc.pos, b.from, tip) >= spec.width + npc.radius
        || (box && (contact === null || contact >= length))) return true;
      b.struck.push(npc.id);
      return false;
    });
    alive.push(b);
  }
  s.beams = alive;
}

/** Blasts have done their work when they appear; this only lets them fade. */
export function stepBlasts(s: State): void {
  for (const b of s.blasts) b.ttlTicks--;
  s.blasts = s.blasts.filter((b) => b.ttlTicks > 0);
}
